// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Center, Group, Loader, Stack, Tabs } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Encounter, Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconRefresh } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { OrderNewTestModal } from '../../components/encounter/OrderNewTestModal';
import { PageHeader } from '../../components/layout/PageHeader';
import { useActiveHSATTests } from '../../hooks/useActiveHSATTests';
import { useHSATDevices } from '../../hooks/useHSATDevices';
import { useRole } from '../../hooks/useRole';
import { botIdentifier } from '../../lib/bot-identifier';
import { hasPermission } from '../../utils/permissions';
import { OrderHSATModal } from '../hsat/OrderHSATModal';
import { HSATDevicesView } from './HSATDevicesView';
import { OrdersWorklist } from './OrdersWorklist';

export function OrdersPage(): JSX.Element {
  const medplum = useMedplum();
  const { role, isAdmin } = useRole();
  const canOrder = hasPermission({ role, permission: 'place-hsat-order', isAdmin });
  const { entries, loading, refresh: refreshDevices } = useHSATDevices();
  const { entries: activeEntries, refresh: refreshActive } = useActiveHSATTests();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orderModalOpened, setOrderModalOpened] = useState(false);
  const [orderPatient, setOrderPatient] = useState<Patient>();
  const [orderEncounter, setOrderEncounter] = useState<Encounter>();
  const [testOrderModalOpened, setTestOrderModalOpened] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const view = searchParams.get('view') === 'hsat' ? 'hsat' : 'orders';

  async function handleSyncReports(): Promise<void> {
    setSyncing(true);
    try {
      await medplum.executeBot(botIdentifier('poll-hsat-diagnostic-reports'), {});
      showNotification({ color: 'green', message: 'Diagnostic reports synced successfully.' });
      refreshDevices();
      refreshActive();
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSyncing(false);
    }
  }

  const handlePatientSelected = async (patient: Patient, encounter: Encounter): Promise<void> => {
    setOrderPatient(patient);
    setOrderEncounter(encounter);
    setTestOrderModalOpened(true);
  };

  return (
    <>
      <OrderHSATModal
        opened={orderModalOpened}
        onClose={() => setOrderModalOpened(false)}
        onPatientSelected={handlePatientSelected}
      />
      {orderPatient && orderEncounter && (
        <OrderNewTestModal
          opened={testOrderModalOpened}
          onClose={() => {
            setTestOrderModalOpened(false);
            setOrderPatient(undefined);
            setOrderEncounter(undefined);
          }}
          encounter={orderEncounter}
          patient={orderPatient}
          practitioner={medplum.getProfile() as Practitioner | undefined}
        />
      )}

      <Stack gap="md" m="md">
        <PageHeader
          title="Orders"
          actions={
            view === 'hsat' ? (
              <Group gap="xs">
                <Button
                  variant="default"
                  size="sm"
                  leftSection={<IconRefresh size={14} />}
                  loading={syncing}
                  onClick={handleSyncReports}
                >
                  Sync Reports
                </Button>
                {canOrder && (
                  <Button variant="filled" size="sm" onClick={() => setOrderModalOpened(true)}>
                    Order
                  </Button>
                )}
              </Group>
            ) : undefined
          }
        />
        <Tabs value={view} onChange={(value) => setSearchParams(value === 'hsat' ? { view: 'hsat' } : {})}>
          <Tabs.List>
            <Tabs.Tab value="orders">Orders</Tabs.Tab>
            <Tabs.Tab value="hsat">HSAT devices</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="orders" pt="md">
            <OrdersWorklist />
          </Tabs.Panel>
          <Tabs.Panel value="hsat" pt="md">
            {loading ? (
              <Center h={300}>
                <Loader size="md" />
              </Center>
            ) : (
              <HSATDevicesView entries={entries} activeEntries={activeEntries} />
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </>
  );
}
